// Compound-keyword remainder pass — ONE shared mechanism for the caret that
// sits inside a multi-word keyword.
//
// THE BUG THIS EXISTS FOR. Every keyword classifier in this codebase reads the
// token before the caret and answers with the words that may follow it. That is
// exactly right while the user types single words, and exactly wrong the moment
// a keyword is spelled with two of them: at `ROWS BETWEEN CURRENT R▮` the
// partial `R` is scored as a FRESH word and the preceding `CURRENT` is thrown
// away, so the answer is `ROWS` / `RANGE` — the frame units — while the one
// legal continuation, `ROW`, is missing. The same shape breaks
// `UNBOUNDED P▮`, `NEXT V▮`, `REFRESH ON D▮`, `READS S▮` and
// `ON DUPLICATE K▮` across all five engines.
//
// THE MECHANISM. When the caret is attached to a partial word, read the
// adjacent bareword(s) before it and ask whether `<those words> <partial>` is a
// PREFIX of a multi-word keyword the engine spells. If it is, offer the
// compound's REMAINDER (`ROW`, `PRECEDING`, `VALUE FOR`, `DEMAND`, `SQL DATA`,
// `KEY UPDATE`) as a tier-1 keyword candidate. The remainder replaces the typed
// partial — the word Monaco's range covers — so accepting `ROW` at
// `CURRENT R▮` yields `CURRENT ROW`, and accepting `VALUE FOR` at `NEXT V▮`
// yields `NEXT VALUE FOR`.
//
// Engine-agnostic by construction: the vocabulary comes from
// `context/legalNextKeywords.ts` (every multi-word entry, across every slot,
// for THAT engine) plus the per-engine `<engine>Keywords.ts` compound lists for
// compounds no slot models. Nothing here knows about a specific keyword.
//
// WHAT IT DOES NOT DO:
//   • a BARE caret (no partial) yields nothing — the pass is only about the
//     word under the cursor, so every un-typed position answers exactly as
//     before;
//   • a preceding word that heads no compound in the engine's vocabulary
//     yields nothing;
//   • a partial that is not a prefix of the compound's next word yields
//     nothing;
//   • a clause-opening keyword standing in the clause it opened is not treated
//     as a compound head — see {@link CLAUSE_OPENER_KINDS}.
//
// PURE text scan (via `slotTextScan.ts`, so comments / literals / delimited
// identifiers never count as words). No parse, no async — safe on the resolver
// hot path; the per-engine vocabulary is built once and memoised.

import type {
  CompletionCandidate,
  CompletionPositionKind,
  DatabaseEngineId,
} from '@/core/engines';

import {
  ALL_NEXT_KEYWORD_SLOTS,
  getLegalNextKeywords,
} from '../context/legalNextKeywords';
import {
  scanOptionsFor,
  scanRegion,
  slotScanEnd,
  type WordSpan,
} from '../context/slotTextScan';

import { MSSQL_COMPOUND_KEYWORDS } from './mssqlKeywords';
import { MYSQL_COMPOUND_KEYWORDS } from './mysqlKeywords';
import { ORACLE_COMPOUND_KEYWORDS } from './oracleKeywords';
import { PGSQL_COMPOUND_KEYWORDS } from './pgsqlKeywords';
import { SQLITE_COMPOUND_KEYWORDS } from './sqliteKeywords';

/** Longest head the scan will match. The longest compounds any engine spells
 *  are four words (`ON DUPLICATE KEY UPDATE`, `LOCK IN SHARE MODE`,
 *  `TABLE IF NOT EXISTS`), whose last word is reached from a three-word head —
 *  so three covers the whole vocabulary. */
const MAX_HEAD_WORDS = 3;

/**
 * Order-hint base for the emitted remainders. Position-aware keyword emission
 * hints from 0 upward, and `orderHint` sorts lower-first, so a negative base
 * puts "finish the word you are typing" ahead of every fresh continuation the
 * slot offers — at `CURRENT R▮` the frame units `RANGE` / `ROWS` are still in
 * the list, but `ROW` leads it.
 */
const REMAINDER_ORDER_BASE = -100;

/**
 * Clause-opening keywords and the position kinds each one opens.
 *
 * A clause opener standing immediately before the caret is doing its
 * clause-opening job, not heading a compound: at `JOIN o ON c▮` the user wants
 * a column, and `ON CONFLICT` (PG / SQLite) or `ON COMMIT` (Oracle) would put
 * `CONFLICT` / `COMMIT` above every column at tier 1. The same `ON` in a
 * position the clause machine does NOT call a join / predicate head — Oracle's
 * `CREATE MATERIALIZED VIEW … REFRESH ON D▮`, which classifies `'unknown'` —
 * keeps its compounds, because there the word really is a compound head.
 *
 * Only the word IMMEDIATELY before the partial is tested: at
 * `… ON DUPLICATE K▮` that word is `DUPLICATE`, so the mysql upsert tail
 * completes even though the compound itself starts with `ON`.
 */
const CLAUSE_OPENER_KINDS: ReadonlyMap<string, ReadonlySet<CompletionPositionKind>> = new Map<
  string,
  ReadonlySet<CompletionPositionKind>
>([
  ['SELECT', new Set(['select-list'])],
  ['FROM', new Set(['from'])],
  ['WHERE', new Set(['where', 'predicate-head'])],
  ['ON', new Set(['join-on', 'predicate-head'])],
  ['HAVING', new Set(['having', 'predicate-head'])],
  ['SET', new Set(['set'])],
  ['VALUES', new Set(['values'])],
  ['RETURNING', new Set(['returning'])],
  ['ORDER', new Set(['order-by'])],
  ['GROUP', new Set(['group-by'])],
  ['BY', new Set(['group-by', 'order-by'])],
  ['LIMIT', new Set(['limit-offset'])],
  ['OFFSET', new Set(['limit-offset'])],
  ['FETCH', new Set(['limit-offset'])],
]);

export type CompoundRemainder = {
  /** The whole keyword, e.g. `CURRENT ROW`. */
  readonly compound: string;
  /** The words already written before the caret, e.g. `CURRENT`. */
  readonly head: string;
  /** What is offered — the compound minus its head, e.g. `ROW`. */
  readonly remainder: string;
  /** The characters typed at the caret, e.g. `R`. */
  readonly partial: string;
};

/** A vocabulary entry, pre-split so the per-keystroke scan compares words
 *  without allocating. */
type CompoundEntry = {
  readonly compound: string;
  readonly words: readonly string[];
};

const vocabularyCache = new Map<string, readonly CompoundEntry[]>();

const EXTRA_COMPOUNDS: Readonly<Record<string, readonly string[]>> = {
  mssql: MSSQL_COMPOUND_KEYWORDS,
  pgsql: PGSQL_COMPOUND_KEYWORDS,
  mysql: MYSQL_COMPOUND_KEYWORDS,
  oracle: ORACLE_COMPOUND_KEYWORDS,
  sqlite: SQLITE_COMPOUND_KEYWORDS,
};

/**
 * Every multi-word keyword an engine spells, upper-cased and
 * single-space-normalised. Built from the per-slot legal-keyword model plus the
 * engine's own compound list, then memoised — `getLegalNextKeywords` is pure,
 * so one pass over the slots per engine per process is enough, and the scan on
 * the keystroke path only walks the finished array.
 */
function compoundEntries(engine: DatabaseEngineId): readonly CompoundEntry[] {
  const cached = vocabularyCache.get(engine);
  if (cached) return cached;
  const seen = new Set<string>();
  const out: CompoundEntry[] = [];
  const add = (raw: string): void => {
    const kw = raw.trim().replace(/\s+/g, ' ').toUpperCase();
    // Single words are not compounds; a parenthesised option
    // (`(FORMAT JSON)`) and a comma list (`INSERT, UPDATE`) are not typed one
    // bareword at a time, so neither can be completed from a head + partial.
    if (!kw.includes(' ') || /[^A-Z0-9_ ]/.test(kw)) return;
    if (seen.has(kw)) return;
    seen.add(kw);
    out.push({ compound: kw, words: kw.split(' ') });
  };
  for (const slot of ALL_NEXT_KEYWORD_SLOTS) {
    // Both option shapes: the conditional tokens `getLegalNextKeywords` strips
    // per `opts` (`AS` after an aliased relation, `INTO` after a star
    // projection) never change the COMPOUND set, but asking for both costs
    // nothing at build time and keeps the vocabulary complete if that changes.
    for (const opts of [{}, { relationHasAlias: true, projectionIsStar: true }]) {
      for (const kw of getLegalNextKeywords(engine, slot, opts)) add(kw);
    }
  }
  for (const kw of EXTRA_COMPOUNDS[engine] ?? []) add(kw);
  vocabularyCache.set(engine, out);
  return out;
}

/** The engine's multi-word keywords. Exposed for tests and for callers that
 *  want the vocabulary itself rather than a caret's matches. Memoised through
 *  {@link compoundEntries}, so repeated calls return the same array. */
const labelCache = new Map<string, readonly string[]>();

export function compoundKeywordVocabulary(engine: DatabaseEngineId): readonly string[] {
  const cached = labelCache.get(engine);
  if (cached) return cached;
  const labels = compoundEntries(engine).map((entry) => entry.compound);
  labelCache.set(engine, labels);
  return labels;
}

/**
 * The compounds `<preceding words> <partial>` is a prefix of, at this caret.
 * Empty whenever any of the guards in this module's header applies.
 *
 * `positionKind` is optional: without it the clause-opener guard cannot run and
 * the scan is vocabulary-only. Resolvers always have it on `ctx`.
 */
export function findCompoundKeywordRemainders(
  engine: DatabaseEngineId,
  text: string,
  offset: number,
  statementStart: number,
  positionKind?: CompletionPositionKind,
): readonly CompoundRemainder[] {
  // `slotScanEnd` is the shared "is this caret classifiable" test: it returns
  // the partial's start after whitespace or inside a freshly-started word, and
  // null when the partial does not START a token — which is what keeps a
  // qualified reference (`t.CURRENT`, `"CURRENT"`) out of here.
  const partialStart = slotScanEnd(text, offset, statementStart);
  if (partialStart === null) return [];
  const end = Math.min(offset, text.length);
  const partial = text.slice(partialStart, end);
  // A bare caret makes no claim about a word — every position keeps today's
  // answer.
  if (partial.length === 0) return [];
  const partialUpper = partial.toUpperCase();

  const heads = adjacentHeadWords(text, partialStart, statementStart, engine);
  if (heads.length === 0) return [];

  // The clause-opener guard, on the word touching the caret.
  const nearest = heads[heads.length - 1]!.word;
  if (positionKind && CLAUSE_OPENER_KINDS.get(nearest)?.has(positionKind)) return [];

  const vocabulary = compoundEntries(engine);
  const out: CompoundRemainder[] = [];
  const seenRemainders = new Set<string>();
  // Longest head first: the more of the compound the user has already written,
  // the more specific the match.
  for (let take = heads.length; take >= 1; take -= 1) {
    const first = heads[heads.length - take]!.word;
    for (const { compound, words } of vocabulary) {
      // Cheapest discriminator first — the vast majority of entries fail it.
      if (words[0] !== first || words.length <= take) continue;
      let matches = true;
      for (let i = 1; i < take; i += 1) {
        if (words[i] !== heads[heads.length - take + i]!.word) {
          matches = false;
          break;
        }
      }
      if (!matches) continue;
      if (!words[take]!.startsWith(partialUpper)) continue;
      const remainder = words.slice(take).join(' ');
      if (seenRemainders.has(remainder)) continue;
      seenRemainders.add(remainder);
      out.push({
        compound,
        head: words.slice(0, take).join(' '),
        remainder,
        partial,
      });
    }
  }
  return out;
}

/**
 * Up to {@link MAX_HEAD_WORDS} barewords immediately before `partialStart`, in
 * source order, and only while they stay ADJACENT — separated from each other
 * and from the partial by whitespace alone. A punctuation mark between two
 * words (`(`, `,`, an operator) means they are not one keyword being typed, so
 * the walk stops there, and so does a comment or a literal (`scanRegion` never
 * reports words inside one, and the gap they leave is not whitespace).
 *
 * A word reached through a `.` or a quote is a NAME (`t.current`, `"CURRENT"`),
 * never a keyword head, so the walk stops before it too.
 */
function adjacentHeadWords(
  text: string,
  partialStart: number,
  statementStart: number,
  engine: DatabaseEngineId,
): readonly WordSpan[] {
  const tokens = scanRegion(text, statementStart, partialStart, scanOptionsFor(engine)).tokens;
  if (tokens.length === 0) return [];
  const heads: WordSpan[] = [];
  let boundary = partialStart;
  for (let i = tokens.length - 1; i >= 0 && heads.length < MAX_HEAD_WORDS; i -= 1) {
    const token = tokens[i]!;
    const tokenEnd = token.start + token.word.length;
    // Everything between this word and what follows it must be whitespace.
    if (tokenEnd > boundary || !isAllWhitespace(text, tokenEnd, boundary)) break;
    if (startsQualifiedName(text, token.start, statementStart)) break;
    heads.unshift(token);
    boundary = token.start;
  }
  return heads;
}

/** True when the char before `at` makes the word there a qualified / quoted
 *  NAME rather than a bareword keyword. */
function startsQualifiedName(text: string, at: number, statementStart: number): boolean {
  if (at <= statementStart) return false;
  const prev = text[at - 1] ?? '';
  return prev === '.' || prev === '"' || prev === '`' || prev === '[';
}

function isAllWhitespace(text: string, from: number, to: number): boolean {
  for (let i = from; i < to; i += 1) {
    if (!/\s/.test(text[i] ?? '')) return false;
  }
  return true;
}

/**
 * Push the compound remainders at this caret onto a resolver's candidate list.
 * Returns true when anything was emitted.
 *
 * Tier 1 with `orderHint` ahead of the position's own keywords: finishing the
 * keyword the user is mid-way through is the only thing legal at that caret.
 * `filterText` is the typed partial so Monaco's own filter keeps a multi-word
 * label (`VALUE FOR`) that the typed word only prefixes.
 *
 * ADDITIVE on purpose — it never short-circuits the resolver. The head word may
 * legitimately be an identifier (`SELECT current R▮` could be the column
 * `current` with an alias), so suppressing the rest of the list on the strength
 * of a vocabulary match would trade one wrong answer for another.
 */
/**
 * Upper-cased labels already offered as KEYWORD candidates.
 *
 * The generic curated keyword list is emitted late and unconditionally, so it
 * duplicates any keyword an earlier, more specific emission already pushed —
 * visibly, once compound remainders arrived: pgsql carries `PRECEDING` in its
 * curated list, so `UNBOUNDED P▮` offered it twice (tier 1 from the remainder
 * pass, tier 7 from the list). Each engine's `emitKeywords` skips a label in
 * this set.
 *
 * Keyword-kind candidates only — a table or column that happens to share a
 * keyword's name is a different suggestion and must not suppress it.
 */
export function keywordLabelsAlreadyOffered(
  out: readonly CompletionCandidate[],
): ReadonlySet<string> {
  const labels = new Set<string>();
  for (const candidate of out) {
    if (candidate.kind === 'keyword') labels.add(candidate.label.toUpperCase());
  }
  return labels;
}

export function emitCompoundKeywordRemainders(
  out: CompletionCandidate[],
  engine: DatabaseEngineId,
  text: string,
  offset: number,
  statementStart: number,
  positionKind?: CompletionPositionKind,
): boolean {
  const found = findCompoundKeywordRemainders(engine, text, offset, statementStart, positionKind);
  if (found.length === 0) return false;
  found.forEach((hit, i) => {
    out.push({
      label: hit.remainder,
      insertText: hit.remainder,
      isSnippet: false,
      kind: 'keyword',
      tier: 1,
      detail: `SQL keyword — ${hit.compound}`,
      filterText: hit.partial,
      orderHint: REMAINDER_ORDER_BASE + i,
    });
  });
  return true;
}
